import { isoToGermanDate } from "../../../../utils/dateTools";
import { dateFormatter, DAY_COLORS } from "./constants";

const MS_PER_DAY = 86400000;

/** Count tickets per day */
function countByDay(ticketList) {
  const counts = {};
  for (const ticket of ticketList) {
    const key = ticket.createdAt.substring(0, 10);
    counts[key] = (counts[key] || 0) + 1;
  }
  return counts;
}

/** Pre-calculate cumulative sum for tickets before a given start key */
function calcPreSum(sortedDates, dailyCounts, startKey) {
  let sum = 0;
  for (const date of sortedDates) {
    if (date < startKey) sum += dailyCounts[date];
    else break;
  }
  return sum;
}

/** Get the first ticket date (ISO string) from a ticket list */
export function getFirstTicketDate(ticketList) {
  if (!ticketList?.length) return null;
  let min = ticketList[0].createdAt.substring(0, 10);
  for (const ticket of ticketList) {
    const d = ticket.createdAt.substring(0, 10);
    if (d < min) min = d;
  }
  return min;
}

/**
 * Build offset-based ticket counts for comparison mode.
 * Each ticket's day is expressed as an offset from eventStartDate.
 * Negative offsets = days before event, 0 = event start day, positive = after.
 * Returns { offsetCounts, minOffset, maxOffset }.
 */
export function processTicketsForComparison(ticketList, eventStartDate) {
  if (!ticketList?.length) return { offsetCounts: {}, minOffset: 0, maxOffset: 0 };

  const refDateStr = eventStartDate || getFirstTicketDate(ticketList);
  if (!refDateStr) return { offsetCounts: {}, minOffset: 0, maxOffset: 0 };

  const refDate = new Date(refDateStr);
  const offsetCounts = {};

  for (const ticket of ticketList) {
    const ticketDate = new Date(ticket.createdAt.substring(0, 10));
    const offset = Math.floor((ticketDate - refDate) / MS_PER_DAY);
    offsetCounts[offset] = (offsetCounts[offset] || 0) + 1;
  }

  const keys = Object.keys(offsetCounts).map(Number);
  if (!keys.length) return { offsetCounts: {}, minOffset: 0, maxOffset: 0 };

  return {
    offsetCounts,
    minOffset: Math.min(...keys),
    maxOffset: Math.max(...keys),
  };
}

/**
 * Build aligned daily/cumulative arrays from offsetCounts within a given range.
 * Tickets before rangeStart are included in the cumulative pre-sum.
 */
export function buildAlignedArrays(offsetCounts, rangeStart, rangeEnd) {
  const daily = [];
  const cumulative = [];
  let sum = 0;

  // Pre-sum for tickets before the visible range
  for (const [offset, count] of Object.entries(offsetCounts)) {
    if (Number(offset) < rangeStart) sum += count;
  }

  for (let i = rangeStart; i <= rangeEnd; i++) {
    const count = offsetCounts[i] || 0;
    sum += count;
    daily.push(count);
    cumulative.push(sum);
  }

  return { daily, cumulative };
}

/**
 * Build chart data for a single event.
 * - Start: max(firstTicket, 90 days before event), but never after endDate
 * - End: last event day (past events) or today (future events)
 * - Optional dateFrom/dateTo overrides via options parameter
 */
export function buildSingleEventData(tickets, eventDays, t, options = {}) {
  const dailyCounts = countByDay(tickets);
  const sortedDates = Object.keys(dailyCounts).sort();
  if (!sortedDates.length) return null;

  const firstTicketDate = new Date(sortedDates[0]);
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  // End date
  let endDate;
  if (options.dateTo) {
    endDate = new Date(options.dateTo);
    endDate.setHours(23, 59, 59, 999);
  } else if (eventDays?.length) {
    const lastEventDay = new Date(eventDays[eventDays.length - 1]);
    lastEventDay.setHours(23, 59, 59, 999);
    endDate = lastEventDay < today ? lastEventDay : today;
  } else {
    endDate = today;
  }

  // Start date
  let startDate;
  if (options.dateFrom) {
    startDate = new Date(options.dateFrom);
  } else if (eventDays?.length) {
    const windowStart = new Date(eventDays[0]);
    windowStart.setDate(windowStart.getDate() - 90);
    startDate = firstTicketDate > windowStart ? firstTicketDate : windowStart;
  } else {
    startDate = firstTicketDate;
  }
  // Fix: if 90-day window starts after endDate (event far in future), fall back to first ticket
  if (startDate > endDate) {
    startDate = firstTicketDate;
  }

  const startKey = startDate.toISOString().substring(0, 10);
  const preSum = calcPreSum(sortedDates, dailyCounts, startKey);

  const labels = [];
  const daily = [];
  const cumulative = [];
  let sum = preSum;

  const todayKey = new Date().toISOString().substring(0, 10);
  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    const key = d.toISOString().substring(0, 10);
    const count = dailyCounts[key] || 0;
    sum += count;
    labels.push(key === todayKey ? t("Today") : dateFormatter.format(d));
    daily.push(count);
    cumulative.push(sum);
  }

  const datasets = [
    {
      label: t("Daily tickets"),
      data: daily,
      borderColor: "rgba(59, 130, 246, 1)",
      backgroundColor: "rgba(59, 130, 246, 0.2)",
      yAxisID: "y",
      fill: true,
      tension: 0.3,
      pointRadius: daily.length > 60 ? 0 : 3,
    },
    {
      label: t("Cumulative"),
      data: cumulative,
      borderColor: "rgba(16, 185, 129, 1)",
      backgroundColor: "rgba(16, 185, 129, 0.1)",
      yAxisID: "y1",
      fill: false,
      tension: 0.3,
      pointRadius: cumulative.length > 60 ? 0 : 3,
      borderDash: [5, 5],
    },
  ];

  // Per-day breakdown: cumulative bookings for each event day
  if (eventDays?.length > 1) {
    eventDays.forEach((day, idx) => {
      const color = DAY_COLORS[idx % DAY_COLORS.length];
      const dayTickets = tickets.filter(t => t.eventDays?.includes(day));
      const dayCounts = countByDay(dayTickets);

      let dayPreSum = 0;
      for (const date of Object.keys(dayCounts).sort()) {
        if (date < startKey) dayPreSum += dayCounts[date];
        else break;
      }

      const dayData = [];
      let daySum = dayPreSum;
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        const key = d.toISOString().substring(0, 10);
        daySum += dayCounts[key] || 0;
        dayData.push(daySum);
      }

      datasets.push({
        label: `${isoToGermanDate(day)} - ${t("Cumulative")}`,
        data: dayData,
        borderColor: color.border,
        backgroundColor: color.bg,
        yAxisID: "y1",
        fill: false,
        tension: 0.3,
        pointRadius: dayData.length > 60 ? 0 : 3,
        borderDash: [3, 3],
        hidden: true,
      });
    });
  }

  return { labels, datasets };
}
